import { useState } from 'react'
import {
  MapPin,
  Package,
  Scale,
  ChevronDown,
  ChevronUp,
  Database,
  ExternalLink,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { MetricCard } from '../primitives'

interface Activity {
  name: string
  location: string
  unit: string
  code: string
  reference_product?: string
  relevance_score?: number
  source?: string
  ecoquery_url?: string | null
}

interface SearchActivitiesData {
  ecoinvent_activities?: Activity[]
  custom_activities?: Activity[]
  total_ecoinvent?: number
  total_custom?: number
  query_time_ms?: number
  query?: string
  filters?: {
    location?: string
    unit?: string
    database?: string
  }
}

interface SearchActivitiesRendererProps {
  data: SearchActivitiesData
}

function ActivityCard({ activity, isCustom = false }: { activity: Activity; isCustom?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-md border p-2 space-y-1',
        isCustom ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/20',
      )}
    >
      {/* Activity name - clickable if ecoquery_url exists */}
      <div className="flex items-start gap-2">
        <Database className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
        {activity.ecoquery_url ? (
          <a
            href={activity.ecoquery_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium leading-tight line-clamp-2 text-primary hover:underline inline-flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {activity.name}
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
        ) : (
          <span className="text-sm font-medium leading-tight line-clamp-2">{activity.name}</span>
        )}
      </div>

      {/* Metadata row */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 ml-5.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {activity.location}
        </span>
        <span className="inline-flex items-center gap-1">
          <Scale className="w-3 h-3" />
          {activity.unit}
        </span>
        {activity.reference_product && (
          <span className="inline-flex items-center gap-1">
            <Package className="w-3 h-3" />
            {activity.reference_product}
          </span>
        )}
      </div>

      {/* Code */}
      <div className="ml-5.5">
        <code className="text-[10px] font-mono text-muted-foreground bg-muted/50 px-1 py-0.5 rounded">
          {activity.code}
        </code>
      </div>
    </div>
  )
}

export function SearchActivitiesRenderer({ data }: SearchActivitiesRendererProps) {
  const [showAll, setShowAll] = useState(false)

  const ecoinventActivities = data.ecoinvent_activities || []
  const customActivities = data.custom_activities || []
  const totalResults = ecoinventActivities.length + customActivities.length

  const maxVisible = 3
  const visibleEcoinvent = showAll ? ecoinventActivities : ecoinventActivities.slice(0, maxVisible)
  const hasMore = totalResults > maxVisible

  return (
    <div className="space-y-3">
      {/* Summary metrics */}
      <div className="flex flex-wrap gap-2">
        <MetricCard
          label="Results"
          value={totalResults}
          variant={totalResults > 0 ? 'success' : 'warning'}
          size="sm"
        />
        {data.query_time_ms && (
          <MetricCard
            label="Search Time"
            value={Math.round(data.query_time_ms)}
            unit="ms"
            size="sm"
          />
        )}
        {data.filters?.database && (
          <MetricCard
            label="Database"
            value={data.filters.database.replace('ecoinvent-', '').replace('-cutoff', '')}
            size="sm"
          />
        )}
      </div>

      {/* Query info */}
      {data.query && (
        <div className="text-xs text-muted-foreground">
          Search: "<span className="font-medium">{data.query}</span>"
          {data.filters?.location && ` in ${data.filters.location}`}
        </div>
      )}

      {/* Custom activities first (if any) */}
      {customActivities.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-primary uppercase tracking-wide">
            Custom Activities ({customActivities.length})
          </h4>
          <div className="space-y-2">
            {customActivities.map((activity, i) => (
              <ActivityCard key={activity.code || i} activity={activity} isCustom />
            ))}
          </div>
        </div>
      )}

      {/* Ecoinvent activities */}
      {ecoinventActivities.length > 0 && (
        <div className="space-y-2">
          {customActivities.length > 0 && (
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Ecoinvent Activities ({ecoinventActivities.length})
            </h4>
          )}
          <div className="space-y-2">
            {visibleEcoinvent.map((activity, i) => (
              <ActivityCard key={activity.code || i} activity={activity} />
            ))}
          </div>
        </div>
      )}

      {/* No results message */}
      {totalResults === 0 && (
        <div className="text-sm text-muted-foreground text-center py-2">
          No activities found. Try different search terms.
        </div>
      )}

      {/* Show more/less */}
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {showAll ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Show {totalResults - maxVisible} more
            </>
          )}
        </button>
      )}
    </div>
  )
}
